import { defineMessages } from '@/hooks/useTranslation';

export default defineMessages({
  header: {
    title: {
      de: 'Sales Cockpit',
      en: 'Sales Cockpit'
    },
    description: {
      de: 'Vertriebssteuerung und -optimierung mit unserem Sales Cockpit.',
      en: 'Sales management and optimization with our Sales Cockpit.'
    },
    mainTitle: {
      de: 'Salescockpit – Die digitale Auftragserfassung für den Außendienst',
      en: 'Sales Cockpit – The digital order entry system for field service'
    },
    titleHighlight: {
      de: 'Auftragserfassung',
      en: 'The digital order entry system'
    },
    subtitle: {
      de: 'Weniger Zettelwirtschaft, mehr Umsatz – Aufträge direkt digital erfassen',
      en: 'Less paperwork, more sales – Capture orders digitally'
    },
    details: {
      de: 'Mit Salescockpit erfasst dein Außendienst Bestellungen und Kundeninfos direkt auf dem Handy oder Tablet. Nahtlose Integration in dein ERP-System für maximale Effizienz.',
      en: 'With Sales Cockpit, your field service captures orders and customer information directly on their phone or tablet. Seamless integration into your ERP system for maximum efficiency.'
    },
    primaryCTA: {
      de: 'Mehr erfahren',
      en: 'Learn more'
    },
    secondaryCTA: {
      de: 'Beratungsgespräch vereinbaren',
      en: 'Schedule a consultation'
    }
  },
  features: {
    title: {
      de: 'Funktionen des Salescockpit',
      en: 'Sales Cockpit Features'
    },
    items: {
      digitalOrder: {
        title: {
          de: 'Digitale Auftragserfassung in Sekunden',
          en: 'Digital order entry in seconds'
        },
        description: {
          de: 'Außendienstmitarbeiter geben Bestellungen direkt per Tablet oder Smartphone ein – mit automatischen Produktempfehlungen & Preisvorschlägen.',
          en: 'Field service staff enter orders directly via tablet or smartphone – with automatic product recommendations & price suggestions.'
        }
      },
      integration: {
        title: {
          de: 'Nahtlose Integration mit Microsoft Business Central',
          en: 'Seamless integration with Microsoft Business Central'
        },
        description: {
          de: 'Alle Auftragsdaten werden direkt ins ERP übermittelt – keine doppelte Dateneingabe, keine Verzögerungen, keine Fehler. Wir unterstützen auch die Integration anderer ERP-Systeme.',
          en: 'All order data is transmitted directly to the ERP – no duplicate data entry, no delays, no errors.'
        }
      },
      realtimeData: {
        title: {
          de: 'Echtzeit-Daten zu Kunden & Bestellungen',
          en: 'Real-time data on customers & orders'
        },
        description: {
          de: 'Greife jederzeit auf aktuelle Preise, Lagerbestände und vergangene Bestellungen zu – perfekt für kundenspezifische Angebote.',
          en: 'Access current prices, inventory levels and past orders at any time – perfect for customer-specific offers.'
        }
      },
      flexibility: {
        title: {
          de: 'Flexible Anpassungen für dein Business',
          en: 'Flexible customization for your business'
        },
        description: {
          de: 'Ob individuelle Formulare, Sonderpreise oder Rabatte – Salescockpit passt sich deinen Prozessen an.',
          en: 'Whether individual forms, special prices or discounts – Sales Cockpit adapts to your processes.'
        }
      },
      offline: {
        title: {
          de: 'Offline-Funktionalität für unterwegs',
          en: 'Offline functionality for on the go'
        },
        description: {
          de: 'Kein Netz? Kein Problem! Bestellungen werden auch offline erfasst und automatisch synchronisiert, sobald eine Verbindung besteht.',
          en: 'No network? No problem! Orders are also recorded offline and automatically synchronized as soon as a connection is established.'
        }
      }
    },
    overview: {
      title: {
        de: 'Die Vorteile auf einen Blick:',
        en: 'The advantages at a glance:'
      },
      fasterProcesses: {
        de: 'Schnellere Prozesse: Bestellungen landen sofort im ERP – kein manuelles Nachtragen mehr.',
        en: 'Faster processes: Orders go directly to the ERP – no more manual entries.'
      },
      fewerErrors: {
        de: 'Weniger Fehler: Automatische Datenübertragung vermeidet Zahlendreher & Fehleingaben.',
        en: 'Fewer errors: Automatic data transfer avoids typing errors & incorrect entries.'
      },
      realtimeData: {
        de: 'Echtzeit-Daten: Der Außendienst sieht sofort Preise, Lagerbestände & Kundenhistorie.',
        en: 'Real-time data: The field service immediately sees prices, inventory levels & customer history.'
      },
      offlineUsable: {
        de: 'Offline nutzbar: Erfassung auch ohne Internet, Synchronisation erfolgt automatisch.',
        en: 'Usable offline: Recording even without internet, synchronization happens automatically.'
      }
    }
  },
  assessment: {
    title: {
      de: 'Ist Salescockpit das richtige Tool für dich?',
      en: 'Is Sales Cockpit the right tool for you?'
    },
    subtitle: {
      de: 'Beantworte diese Fragen mit Ja oder Nein',
      en: 'Answer these questions with Yes or No'
    },
    questions: {
      fieldService: {
        question: {
          de: 'Muss dein Außendienst Bestellungen oder Kundendaten unterwegs erfassen?',
          en: 'Does your field service need to capture orders or customer data on the go?'
        },
        explanation: {
          de: 'Salescockpit ermöglicht die einfache Auftragserfassung per Smartphone oder Tablet – überall und jederzeit.',
          en: 'Sales Cockpit enables easy order entry via smartphone or tablet – anywhere and anytime.'
        }
      },
      erpTransfer: {
        question: {
          de: 'Sollen Auftragsdaten direkt ins ERP übertragen werden – ohne manuellen Mehraufwand?',
          en: 'Should order data be transferred directly to the ERP – without manual extra work?'
        },
        explanation: {
          de: 'Mit Salescockpit werden alle Daten automatisch synchronisiert und direkt ins ERP-System übertragen.',
          en: 'With Sales Cockpit, all data is automatically synchronized and transferred directly to the ERP system.'
        }
      },
      realtimeAccess: {
        question: {
          de: 'Willst du Lagerbestände, Preise & Rabatte in Echtzeit im Außendienst einsehen?',
          en: 'Do you want to view inventory levels, prices & discounts in real time in the field service?'
        },
        explanation: {
          de: 'Salescockpit bietet deinem Außendienst Zugriff auf alle relevanten Daten in Echtzeit.',
          en: 'Sales Cockpit gives your field service access to all relevant data in real time.'
        }
      }
    }
  },
  proof: {
    title: {
      de: 'O-Töne der Entwickler',
      en: 'Insights from the Developers'
    },
    subtitle: {
      de: 'Die Macher von Salescockpit über ihre Motivation und Vision',
      en: 'The makers of Sales Cockpit about their motivation and vision'
    },
    stats: {
      timeSaving: {
        value: {
          de: '86%',
          en: '86%'
        },
        description: {
          de: 'Zeitersparnis bei der Auftragserfassung',
          en: 'Time savings in order entry'
        }
      },
      errorReduction: {
        value: {
          de: '94%',
          en: '94%'
        },
        description: {
          de: 'Reduktion von Übertragungsfehlern',
          en: 'Reduction of transmission errors'
        }
      },
      fasterProcessing: {
        value: {
          de: '63%',
          en: '63%'
        },
        description: {
          de: 'Schnellere Auftragsbearbeitung',
          en: 'Faster order processing'
        }
      },
      increasedVolume: {
        value: {
          de: '47%',
          en: '47%'
        },
        description: {
          de: 'Erhöhte Bestellmenge pro Kunde',
          en: 'Increased order volume per customer'
        }
      }
    },
    testimonials: {
      dennis: {
        quote: {
          de: 'Als Entwickler lag mir besonders die intuitive Bedienung am Herzen. Salescockpit soll so einfach zu nutzen sein, dass sich jeder Außendienstmitarbeiter sofort zurechtfindet – ohne lange Schulungen oder komplizierte Einrichtung.',
          en: 'As a developer, intuitive operation was particularly important to me. Sales Cockpit should be so easy to use that every field service employee can immediately find their way around – without long training sessions or complicated setup.'
        },
        author: {
          de: 'Dennis Dittrich',
          en: 'Dennis Dittrich'
        },
        position: {
          de: 'splusd-solutions',
          en: 'splusd-solutions'
        }
      },
      simon: {
        quote: {
          de: 'Meine Vision war es, die Lücke zwischen Außendienst und ERP-System zu schließen. Mit Salescockpit haben wir eine Lösung geschaffen, die beide Welten nahtlos verbindet und dabei die Arbeitsweise des Außendienstes respektiert.',
          en: 'My vision was to bridge the gap between field service and ERP system. With Sales Cockpit, we have created a solution that seamlessly connects both worlds while respecting the way field service works.'
        },
        author: {
          de: 'Simon Brüggemann',
          en: 'Simon Brüggemann'
        },
        position: {
          de: 'splusd-solutions',
          en: 'splusd-solutions'
        }
      }
    },
    conclusion: {
      de: 'Nicht nur unsere Kunden sind überzeugt – wir stehen mit voller Leidenschaft hinter unserem Produkt.',
      en: 'Not only our customers are convinced – we stand behind our product with full passion.'
    }
  },
  trust: {
    title: {
      de: 'Vertrauen ist die Basis unserer Zusammenarbeit',
      en: 'Trust is the foundation of our collaboration'
    },
    subtitle: {
      de: 'Wir setzen auf langfristige Partnerschaften und garantieren dir maximale Sicherheit und Zufriedenheit.',
      en: 'We focus on long-term partnerships and guarantee you maximum security and satisfaction.'
    },
    guarantees: {
      moneyBack: {
        title: {
          de: '30 Tage Geld-zurück-Garantie',
          en: '30-day money-back guarantee'
        },
        description: {
          de: 'Teste Salescockpit risikofrei: Wenn du nicht vollständig zufrieden bist, erhältst du dein Geld zurück – ohne Wenn und Aber.',
          en: 'Test Sales Cockpit risk-free: If you are not completely satisfied, you get your money back – no questions asked.'
        }
      },
      dataSecurity: {
        title: {
          de: 'Höchste Datensicherheit',
          en: 'Highest data security'
        },
        description: {
          de: 'Deine Daten werden in ISO-27001-zertifizierten deutschen Rechenzentren gespeichert und sind DSGVO-konform geschützt.',
          en: 'Your data is stored in ISO-27001-certified German data centers and is protected in compliance with GDPR.'
        }
      },
      implementation: {
        title: {
          de: 'Schnelle Implementierung',
          en: 'Fast implementation'
        },
        description: {
          de: 'Wir garantieren eine effiziente Implementierung innerhalb des vereinbarten Zeitrahmens – ohne versteckte Kosten.',
          en: 'We guarantee efficient implementation within the agreed timeframe – without hidden costs.'
        }
      },
      support: {
        title: {
          de: 'Support-Garantie',
          en: 'Support guarantee'
        },
        description: {
          de: 'Unser Support-Team steht dir werktags von 8-18 Uhr zur Verfügung – mit garantierter Reaktionszeit von maximal 4 Stunden.',
          en: 'Our support team is available Monday to Friday from 8am-6pm – with guaranteed response time of maximum 4 hours.'
        }
      }
    },
    partners: {
      title: {
        de: 'Partner & Zertifizierungen',
        en: 'Partners & Certifications'
      }
    },
    industries: {
      title: {
        de: 'Salescockpit kann in folgenden Branchen eingesetzt werden',
        en: 'Sales Cockpit can be used in the following industries'
      },
      items: {
        fashion: {
          de: 'Fashion & Textil',
          en: 'Fashion & Textile'
        },
        wholesale: {
          de: 'Großhandel',
          en: 'Wholesale'
        },
        building: {
          de: 'Sanitär/Heizung/Klima',
          en: 'Plumbing/Heating/HVAC'
        },
        automotive: {
          de: 'Elektrogroßhandel',
          en: 'Electrical Wholesale'
        },
        technology: {
          de: 'Lebensmittelgroßhandel',
          en: 'Food Wholesale'
        },
        manufacturing: {
          de: 'Baustoffhandel',
          en: 'Building Materials Trade'
        }
      }
    },
    finalCta: {
      title: {
        de: 'Bereit, deinen Außendienst zu revolutionieren?',
        en: 'Ready to revolutionize your field service?'
      },
      subtitle: {
        de: 'Mit Salescockpit steigerst du Effizienz, reduzierst Fehler und erhöhst den Umsatz – risikofrei dank unserer Garantien.',
        en: 'With Sales Cockpit you increase efficiency, reduce errors and increase sales – risk-free thanks to our guarantees.'
      },
      buttonText: {
        de: 'Jetzt Demo vereinbaren',
        en: 'Schedule a demo now'
      }
    }
  },
  benefits: {
    title: {
      de: 'Vorteile von Salescockpit',
      en: 'Benefits of Sales Cockpit'
    },
    items: {
      digitalOrder: {
        title: {
          de: 'Digitale Auftragserfassung in Sekunden',
          en: 'Digital order entry in seconds'
        },
        description: {
          de: 'Schnelle und fehlerfreie Bestelleingabe.',
          en: 'Fast and error-free order entry.'
        }
      },
      erpIntegration: {
        title: {
          de: 'Direkte ERP-Integration',
          en: 'Direct ERP integration'
        },
        description: {
          de: 'Nahtlose Anbindung an dein Warenwirtschaftssystem.',
          en: 'Seamless connection to your inventory management system.'
        }
      },
      realtimeData: {
        title: {
          de: 'Echtzeit-Daten im Außendienst',
          en: 'Real-time data in the field service'
        },
        description: {
          de: 'Aktuelle Preise, Bestände und Kundeninformationen.',
          en: 'Current prices, inventory levels and customer information.'
        }
      },
      offlineMode: {
        title: {
          de: 'Offline-Funktionalität',
          en: 'Offline functionality'
        },
        description: {
          de: 'Arbeiten auch ohne Internetverbindung möglich.',
          en: 'Working also possible without internet connection.'
        }
      },
      fewerErrors: {
        title: {
          de: 'Weniger Fehler, schnellere Prozesse',
          en: 'Fewer errors, faster processes'
        },
        description: {
          de: 'Reduzierte Fehlerquote durch Automatisierung.',
          en: 'Reduced error rate through automation.'
        }
      },
      optimizedWorkflows: {
        title: {
          de: 'Optimierte Außendienst-Workflows',
          en: 'Optimized field service workflows'
        },
        description: {
          de: 'Effizientere Arbeitsabläufe für deinen Vertrieb.',
          en: 'More efficient workflows for your sales team.'
        }
      },
      customPricing: {
        title: {
          de: 'Kundenspezifische Preisgestaltung',
          en: 'Customer-specific pricing'
        },
        description: {
          de: 'Individuelle Preise und Rabatte für jeden Kunden.',
          en: 'Individual prices and discounts for each customer.'
        }
      },
      customerSatisfaction: {
        title: {
          de: 'Erhöhte Kundenzufriedenheit',
          en: 'Increased customer satisfaction'
        },
        description: {
          de: 'Schnellere Reaktionszeiten und besserer Service.',
          en: 'Faster response times and better service.'
        }
      },
      easyUsage: {
        title: {
          de: 'Einfache, intuitive Bedienung',
          en: 'Simple, intuitive operation'
        },
        description: {
          de: 'Minimale Einarbeitungszeit für deine Mitarbeiter.',
          en: 'Minimal training time for your employees.'
        }
      },
      modular: {
        title: {
          de: 'Modular & erweiterbar',
          en: 'Modular & expandable'
        },
        description: {
          de: 'Flexibel anpassbar an deine Geschäftsprozesse.',
          en: 'Flexibly adaptable to your business processes.'
        }
      }
    }
  },
  callToAction: {
    title: {
      de: 'Kostenloser Salescockpit Demo-Termin',
      en: 'Free Sales Cockpit demo appointment'
    },
    description: {
      de: 'Erlebe selbst, wie Salescockpit deinen Außendienst effizienter macht – wir zeigen dir alle Funktionen live an deinen eigenen Beispieldaten.',
      en: "Experience for yourself how Sales Cockpit makes your field service more efficient – we'll show you all the functions live using your own sample data."
    },
    buttonText: {
      de: 'Demo-Termin vereinbaren',
      en: 'Schedule a demo appointment'
    }
  },
  contact: {
    title: {
      de: 'Hast du Fragen zum Salescockpit?',
      en: 'Do you have questions about Sales Cockpit?'
    },
    description: {
      de: 'Wir beraten dich gerne zu allen Fragen rund um die digitale Auftragserfassung für deinen Außendienst.',
      en: 'We are happy to advise you on all questions regarding digital order entry for your field service.'
    }
  }
}); 